import { createFileRoute, Link } from "@tanstack/react-router";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, LayoutDashboard } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { InstallAppButton } from "@/components/InstallAppButton";

export const Route = createFileRoute("/install")({
  head: () => ({
    meta: [
      { title: `Install the app · ${BRAND.name}` },
      {
        name: "description",
        content: `Add ${BRAND.name} to your home screen for a faster, app-like experience on iOS and Android.`,
      },
      { property: "og:title", content: `Install the app · ${BRAND.name}` },
      {
        property: "og:description",
        content: `Add ${BRAND.name} to your home screen for a faster, app-like experience on iOS and Android.`,
      },
    ],
    links: [{ rel: "canonical", href: `${BRAND.url}/install` }],
  }),
  component: InstallPage,
});

function InstallPage() {
  const url = BRAND.url;
  return (
    <main className="min-h-dvh bg-background text-foreground">
      <div className="border-b border-border">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs tracking-wide-luxury text-muted-foreground hover:text-accent transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to site
          </Link>
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-xs tracking-wide-luxury text-accent hover:text-accent/80 transition-colors"
          >
            <LayoutDashboard className="h-4 w-4" />
            Dashboard
          </Link>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-6 py-12">
        <p className="text-[10px] tracking-wide-luxury text-accent">
          {BRAND.tagline}
        </p>
        <h1 className="mt-3 font-display text-4xl">The Reserve, on your home screen</h1>
        <p className="mt-3 max-w-xl text-sm text-muted-foreground">
          Keep {BRAND.name} a tap away. A clean icon, a full-screen
          experience, and faster access to shifts and bookings — no app store
          required.
        </p>


        <section className="mt-10 grid gap-8 md:grid-cols-[auto_1fr] md:items-start">
          <div className="flex flex-col items-center gap-3 border border-border bg-card p-6">
            <QRCodeSVG
              value={url}
              size={176}
              level="M"
              includeMargin={false}
              bgColor="#FFFFFF"
              fgColor="#2E6B8A"
            />
            <p className="text-[10px] tracking-wide-luxury text-muted-foreground">
              Scan with your phone
            </p>
            <p className="break-all text-center text-xs text-muted-foreground">
              {url}
            </p>
          </div>

          <div className="space-y-8">
            <div>
              <p className="text-[10px] tracking-wide-luxury text-accent">
                On this device
              </p>
              <h2 className="mt-1 font-display text-xl">One-tap install</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Supported browsers will install instantly. On other devices,
                follow the steps below.
              </p>
              <div className="mt-4">
                <InstallAppButton label="Install" />
              </div>
            </div>

            <div>
              <p className="text-[10px] tracking-wide-luxury text-accent">
                iOS · Safari
              </p>
              <h2 className="mt-1 font-display text-xl">iPhone &amp; iPad</h2>
              <ol className="mt-3 space-y-2 text-sm text-muted-foreground">
                <li>1. Open this page in Safari (not an in-app browser).</li>
                <li>
                  2. Tap the <span className="text-foreground">Share</span>{" "}
                  button at the bottom of the screen.
                </li>
                <li>
                  3. Scroll and choose{" "}
                  <span className="text-foreground">Add to Home Screen</span>.
                </li>
                <li>
                  4. Tap <span className="text-foreground">Add</span> in the
                  top-right corner.
                </li>
              </ol>
            </div>

            <div>
              <p className="text-[10px] tracking-wide-luxury text-accent">
                Android · Chrome
              </p>
              <h2 className="mt-1 font-display text-xl">Android</h2>
              <ol className="mt-3 space-y-2 text-sm text-muted-foreground">
                <li>1. Open this page in Chrome.</li>
                <li>
                  2. Tap the <span className="text-foreground">⋮</span> menu in
                  the top-right corner.
                </li>
                <li>
                  3. Choose{" "}
                  <span className="text-foreground">Install app</span> or{" "}
                  <span className="text-foreground">Add to Home screen</span>.
                </li>
                <li>
                  4. Confirm by tapping{" "}
                  <span className="text-foreground">Install</span>.
                </li>
              </ol>
            </div>

            <div>
              <p className="text-[10px] tracking-wide-luxury text-accent">
                Desktop
              </p>
              <h2 className="mt-1 font-display text-xl">Chrome &amp; Edge</h2>
              <ol className="mt-3 space-y-2 text-sm text-muted-foreground">
                <li>
                  1. Click the{" "}
                  <span className="text-foreground">install icon</span> on the
                  right of the address bar.
                </li>
                <li>
                  2. Choose{" "}
                  <span className="text-foreground">Install</span> in the
                  dialog.
                </li>
              </ol>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
